import * as React from 'react'
import { Remove as ExpandLessIcon, Add as ExpandMoreIcon } from '@mui/icons-material'
import { Box, Card, CardContent, CardHeader, Chip, Collapse, IconButton, Table, TableBody, TableRow, TableCell, Typography } from '@mui/material'
import { tableCellClasses } from '@mui/material/TableCell'
import { styled } from '@mui/system'

// Data row styling
const DataRow = styled(TableRow)(({ theme }) => ({
  [`& td`]: {
    borderBottom: 0,
    borderTop: '1px solid ' + theme.palette.divider,
  },
  [`&.custom_meal td, &.custom_wrap td, &.custom_salad td, &.custom_breakfast td`]: {
    fontWeight: theme.typography.fontWeightRegular,
  },
}))

// Data cell styling
const DataCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontWeight: theme.typography.fontWeightMedium,
    verticalAlign: 'top',
  },
  [`&.quantity`]: {
    textAlign: 'right',
    textWrap: 'nowrap',
    width: '50px',
  },
  [`&.customer`]: {
    textAlign: 'center',
    width: '200px',
  },
  [`&:first-of-type`]: {
    paddingLeft: '0',
  },
  [`&:last-of-type`]: {
    paddingRight: '0',
  },
}))

/**
 * OrderItem component
 */
const OrderItem = ({ item }) => {
  return (
    <DataRow className={item.type} hover>
      <DataCell className="quantity">{item.quantity} x</DataCell>
      <DataCell className="item">
        {item.name === 'Custom Meal' ? item.description : item.description ? item.name + ' - ' + item.description : item.name}
        {item.notes && (
          <Typography variant="body2" color="error" sx={{ fontWeight: 'fontWeightBold' }}>
            {item.notes}
          </Typography>
        )}
      </DataCell>
      <DataCell className="customer">{item.customer && <Typography sx={{ bgcolor: item.orderType }}>{item.customer}</Typography>}</DataCell>
    </DataRow>
  )
}

/**
 * OrderNestedGroup component
 */
const OrderNestedGroup = ({ group, showName = true }) => {
  return (
    <Box sx={{ mt: 2 }}>
      {showName && (
        <Typography variant="body2" component="h6" sx={{ fontWeight: 'fontWeightMedium', textTransform: 'uppercase', mb: 1 }}>
          {group.name}
        </Typography>
      )}
      <Table size="small">
        <TableBody>
          {group.items.map((item) => (
            <OrderItem key={item._id} item={item} />
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

/**
 * OrderGroup component
 */
const OrderGroup = ({ group }) => {
  const [expanded, setExpanded] = React.useState(false)

  return (
    <Card variant="outlined" sx={{ breakInside: 'avoid', my: 3 }}>
      <CardHeader
        onClick={(e) => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
        }}
        avatar={
          <IconButton size="small" edge="start" disableRipple={true} sx={{ padding: 0, color: 'text.primary' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
        title={
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ mr: 1, color: 'text.primary' }}>{group.name}</Typography>
            <Chip label={group.total_items} color="success" size="small" />
          </Box>
        }
      />
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          {group.nested_groups.map((nestedGroup) => (
            <OrderNestedGroup key={nestedGroup.name} group={nestedGroup} showName={! (group.nested_groups.length === 1 && nestedGroup.name === 'Others')} />
          ))}
        </CardContent>
      </Collapse>
    </Card>
  )
}

export default OrderGroup
